import React from 'react';

import type {
  MaintenanceListPriceData,
  MaintenanceListTableGroupItem,
} from '@/components/sections/service/maintenance-list/table';
import { MaintenanceListTableBriefBodyPreventive } from '@/components/sections/service/maintenance-list/table/brief/body/preventive';
import { MaintenanceListTableBriefBodyPrices } from '@/components/sections/service/maintenance-list/table/brief/body/prices';
import { MaintenanceListTableBriefBodyPrivileges } from '@/components/sections/service/maintenance-list/table/brief/body/privileges';
import { MaintenanceListTableBriefSpecialInfo } from '@/components/sections/service/maintenance-list/table/brief/body/special-info';
import { MaintenanceListTableBriefBodyTitleValue } from '@/components/sections/service/maintenance-list/table/brief/body/title-value';
import { MaintenanceListTableBriefFooterPriceFixed } from '@/components/sections/service/maintenance-list/table/brief/footer-price-fixed';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import type { IVehicleType } from '@/types/services/contentstack';
import type { RegularMaintenaceSchedule } from '@/types/services/contentstack/services/maintenance-service';

interface MaintenanceListTableBriefBody {
  vehicleType: IVehicleType;
  selectedIndex: number;
  selectedMaintenanceSchedule?: RegularMaintenaceSchedule;
  tableItems: MaintenanceListTableGroupItem[];
  priceData?: MaintenanceListPriceData;
}

const MAINTENANCE_LIST_BRIEF_BODY_TITLE = 'ค่าใช้จ่ายนี้ครอบคลุม';
const MAINTENANCE_LIST_BRIEF_BODY_LIST_TITLE = 'รายการอะไหล่เช็กระยะ';
const MAINTENANCE_LIST_BRIEF_BODY_SUGGESTIONS_TITLE = 'รายการแนะนำเพิ่มเติม';
const MAINTENANCE_LIST_BRIEF_BODY_SUGGESTIONS_ITEMS = [
  'สลับยาง และถ่วงล้อ',
  'แบตเตอรี่',
  'ยางรถยนต์',
];
const SHOW_FOOTER_FIXED_PRICE_ELEMENT_ID = 'under-price';

export const MaintenanceListTableBriefBody = (
  props: MaintenanceListTableBriefBody
) => {
  return (
    <>
      <Box paddingY="3" border="b-1" borderColor="gray300">
        <Container size="sm">
          <Text as="p" font="bold" size="lg">
            {MAINTENANCE_LIST_BRIEF_BODY_TITLE}
          </Text>
        </Container>
      </Box>
      <Box border="b-1" borderColor="gray300">
        <Container size="sm">
          <Box paddingY="3">
            <MaintenanceListTableBriefBodyTitleValue
              label={MAINTENANCE_LIST_BRIEF_BODY_LIST_TITLE}
            />
          </Box>

          <Box paddingLeft="7">
            {props.tableItems.map((group, groupIndex) =>
              group.children?.map(
                (item, childIndex) =>
                  item.values?.[props.selectedIndex] && (
                    <Box key={`group${groupIndex}${childIndex}`} paddingY="3">
                      <Text>{item.labelTitle}</Text>
                    </Box>
                  )
              )
            )}
          </Box>
        </Container>
      </Box>
      <MaintenanceListTableBriefBodyPrivileges
        privileges={props.selectedMaintenanceSchedule?.maintenance_privileges}
      />
      <MaintenanceListTableBriefBodyPrices
        selectedMaintenanceSchedule={props.selectedMaintenanceSchedule}
        priceData={props.priceData}
      />
      {props.vehicleType === 'lcv' ? (
        <>
          {!!props.selectedMaintenanceSchedule?.special_cost_description && (
            <Container size="sm">
              <MaintenanceListTableBriefSpecialInfo
                maintenanceDescription={
                  props.selectedMaintenanceSchedule?.maintenance_description ??
                  ''
                }
                specialCostDescription={
                  props.selectedMaintenanceSchedule?.special_cost_description ??
                  ''
                }
              />
            </Container>
          )}
          <Box
            id={SHOW_FOOTER_FIXED_PRICE_ELEMENT_ID}
            paddingY="3"
            border="b-1"
            borderColor="gray300"
          >
            <Container size="sm">
              <Text as="p" font="bold" size="lg">
                {MAINTENANCE_LIST_BRIEF_BODY_SUGGESTIONS_TITLE}
              </Text>
            </Container>
          </Box>
          <Box paddingLeft="7" border="b-1" borderColor="gray300">
            {MAINTENANCE_LIST_BRIEF_BODY_SUGGESTIONS_ITEMS.map(
              (item, index) => (
                <Box
                  key={`suggestionItem${index}`}
                  paddingY="3"
                  paddingLeft="7"
                >
                  <Text>{item}</Text>
                </Box>
              )
            )}
          </Box>
          {!!props.selectedMaintenanceSchedule?.preventive_maintenance_parts &&
            props.selectedMaintenanceSchedule?.preventive_maintenance_parts
              .length > 0 && (
              <MaintenanceListTableBriefBodyPreventive
                preventives={
                  props.selectedMaintenanceSchedule
                    ?.preventive_maintenance_parts
                }
              />
            )}
          {!props.selectedMaintenanceSchedule?.special_cost_description && (
            <MaintenanceListTableBriefSpecialInfo
              maintenanceDescription={
                props.selectedMaintenanceSchedule?.maintenance_description ?? ''
              }
              specialCostDescription={
                props.selectedMaintenanceSchedule?.special_cost_description
              }
            />
          )}
        </>
      ) : (
        <Box
          id={SHOW_FOOTER_FIXED_PRICE_ELEMENT_ID}
          border="b-1"
          borderColor="gray300"
        />
      )}
      <MaintenanceListTableBriefFooterPriceFixed
        selectedMaintenanceSchedule={props.selectedMaintenanceSchedule}
        elementIdToShow={SHOW_FOOTER_FIXED_PRICE_ELEMENT_ID}
        priceData={props.priceData}
      />
    </>
  );
};
MaintenanceListTableBriefBody.displayName =
  'SectionMaintenanceListTableBriefBody';
